import React from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

interface Props {
  description?: string;
  title?: string;
  isArticle?: boolean;
  createdAt?: string;
  author?: string;
  lang?: string;
  socialImage?: string;
  companyLogo?: string;
  additionalSchemas?: object[];
  resourceUrl?: string;
}

export const Schema: React.FC<Props> = ({
  description = '',
  lang = 'fi',
  title = '',
  author = '',
  socialImage = '',
  createdAt = '',
  companyLogo = '',
  isArticle = false,
  additionalSchemas = [],
  resourceUrl = '',
}) => {
  const {
    site: { siteMetadata },
  } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            siteUrl
            socialImage
            company
            salesEmail
            description
          }
        }
      }
    `
  );

  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    description: description || siteMetadata.description,
    name: siteMetadata.title,
    sameAs: [
      'https://www.linkedin.com/company/taito-united',
      'https://www.instagram.com/taitounited/',
    ],
    url: siteMetadata.siteUrl,
    logo: companyLogo,
    image: `${siteMetadata.siteUrl}${siteMetadata.socialImage}`,
    legalName: siteMetadata.company,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        email: siteMetadata.salesEmail,
        contactType: 'sales',
      },
    ],
    foundingDate: '2011',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Metsänneidonkuja 10',
      extendedAddress: 'Spektri Business Park, Kvartti 2. krs',
      addressLocality: 'Espoo',
      postalCode: '02130',
      addressCountry: 'FI',
    },
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: resourceUrl,
    description: description || siteMetadata.description,
    headline: title,
    publisher: {
      '@type': 'Organization',
      name: siteMetadata.title,
      logo: { '@type': 'ImageObject', url: companyLogo },
    },
    author: author || {
      '@type': 'Organization',
      name: siteMetadata.title,
      logo: { '@type': 'ImageObject', url: companyLogo },
    },
    editor: author || undefined,
    datePublished: createdAt,
    dateCreated: createdAt,
    dateModified: createdAt,
    inLanguage: lang,
    image: socialImage,
  };

  const schema = isArticle ? articleSchema : baseSchema;

  return (
    <Helmet>
      <script type={'application/ld+json'}>{JSON.stringify(schema)}</script>
      {additionalSchemas.map((s, i) => (
        <script key={i} type={'application/ld+json'}>
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default Schema;
